/**
 * Hook per subscription Supabase Realtime
 * - Messaggi (INSERT/UPDATE) via postgres_changes
 * - Presenza (broadcast)
 * - Reactions (broadcast)
 *
 * Pattern allineato a taskRealtimeService/leadsRealtimeService:
 * - Channel name con timestamp per evitare conflitti stale
 * - Subscribe callback con (status, err) per catturare errori
 * - Dipendenza singola su organizationId (store via ref)
 */

import { useEffect, useRef } from 'react';
import { supabase } from '../../supabase';
import { useWhatsAppChatStore } from '../../stores/whatsappChatStore';
import type { Message, MessageType, PresenceStatus } from '../../types/whatsappChat';
import { decryptWhatsAppMessage, isEncrypted } from '../../utils/crypto';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { parseReplyTo } from '@/lib/utils/whatsappReplyTo';
import { dbMessageStatus, normalizeDbMessageType, rowExtras } from '@/lib/services/whatsapp/messageRowMapping';

/**
 * Mappa gli stati WAHA ai nostri stati interni
 * WAHA usa: 'available', 'unavailable', 'composing', 'recording', 'paused', 'online', 'offline'
 * Noi usiamo: 'online', 'offline', 'typing', 'recording', 'paused'
 */
function mapWahaPresenceStatus(wahaStatus: string): PresenceStatus {
  switch (wahaStatus) {
    case 'composing':
    case 'typing':
      return 'typing';
    case 'available':
    case 'online':
      return 'online';
    case 'unavailable':
    case 'offline':
      return 'offline';
    case 'recording':
      return 'recording';
    case 'paused':
      return 'paused';
    default:
      console.log('[Realtime] Unknown WAHA presence status:', wahaStatus);
      return 'offline';
  }
}

export function useWhatsAppRealtime(organizationId: string | undefined) {
  // Usa ref per le funzioni dello store per evitare che cambi di riferimento
  // causino re-subscribe. Solo organizationId deve triggerare re-subscribe.
  const storeRef = useRef(useWhatsAppChatStore.getState());
  const channelsRef = useRef<ReturnType<typeof supabase.channel>[]>([]);

  // Mantieni ref aggiornato con lo stato più recente dello store
  useEffect(() => {
    const unsub = useWhatsAppChatStore.subscribe((state) => {
      storeRef.current = state;
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!organizationId) return;

    // Cleanup previous subscriptions
    for (const ch of channelsRef.current) {
      supabase.removeChannel(ch);
    }
    channelsRef.current = [];

    const ts = Date.now();

    // === 1. MESSAGES CHANNEL (postgres_changes) ===
    const messagesChannel = supabase
      .channel(`wa-messages-${organizationId}-${ts}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'whatsapp_messages',
          filter: `organization_id=eq.${organizationId}`,
        },
        async (payload) => {
          try {
            // Multi-session: filtra messaggi per config_id della sessione attiva
            const activeConfigId = storeRef.current.wahaConfig?.configId;
            if (activeConfigId && payload.new.config_id && payload.new.config_id !== activeConfigId) {
              return;
            }

            const msg = await transformDbPayload(payload.new, organizationId);
            if (activeConfigId !== storeRef.current.wahaConfig?.configId) return;
            console.log('[WA] Realtime INSERT:', msg.direction, msg.chatId?.slice(0, 20), msg.content?.slice(0, 30) || '(media)');

            // addMessage aggiorna anche chats[].lastMessage e riordina la sidebar
            // (via applyMessageToChats), quindi refreshChats() qui sarebbe ridondante.
            // Il polling 15s WAHA della ChatSidebar resta come fallback per la consistenza generale.
            storeRef.current.addMessage(msg);

            // Messaggio in arrivo su una conversazione "Chiusa": torna "Da gestire" anche a
            // schermo (nel DB lo fa il trigger trg_whatsapp_messages_riapri_chat).
            if (msg.type !== 'system' && msg.direction === 'inbound' && msg.chatId) {
              useWhatsAppChatMetaStore.getState().reopenIfClosed(msg.chatId);
            }
          } catch (error) {
            console.error('[Realtime] INSERT handler error:', error);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'whatsapp_messages',
          filter: `organization_id=eq.${organizationId}`,
        },
        (payload) => {
          const newData = payload.new;
          const messageId = newData.id as string;

          if (newData.is_edited) {
            storeRef.current.updateMessageEdited(messageId, newData.content as string);
          } else if (newData.is_deleted) {
            storeRef.current.updateMessageDeleted(messageId);
          } else {
            // Numeri Ufficiale: su un invio fallito arriva anche metadata.error (motivo in chiaro).
            // La trascrizione dei vocali arriva con un UPDATE qualche secondo dopo il messaggio.
            const transcription = typeof newData.transcription === 'string' && newData.transcription ? newData.transcription : undefined;
            storeRef.current.updateMessageStatus(messageId, dbMessageStatus(newData), { ...rowExtras(newData), ...(transcription ? { transcription } : {}) });
          }
        }
      )
      .subscribe((status: string, err?: Error) => {
        if (status === 'SUBSCRIBED') {
          console.log('[Realtime] ✅ Messages channel subscribed for org:', organizationId);
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[Realtime] Channel error for whatsapp_messages:', err?.message);
        } else if (status === 'TIMED_OUT') {
          console.error('[Realtime] Subscription timed out for whatsapp_messages');
        }
      });

    // === 2. PRESENCE CHANNEL (broadcast) ===
    // Nome canale DEVE corrispondere a quello del webhook (presenceHandler.ts)
    const presenceChannel = supabase
      .channel(`presence-${organizationId}`)
      .on('broadcast', { event: 'presence' }, ({ payload }) => {
        console.log('[WA] Presence broadcast:', payload.chatId?.slice(0, 20), payload.presence, payload.participant?.slice(0, 20));
        // FIX loopback: filtra echo dei nostri stessi typing/presence.
        // Quando facciamo setPresence('typing'), WAHA emette un evento presence che
        // torna al webhook → broadcast → frontend → mostrerebbe "il cliente sta scrivendo"
        // anche se il typing è il NOSTRO. Filtrare per phoneNumber della session attiva.
        const ownNumber = storeRef.current.wahaConfig?.phoneNumber;
        if (ownNumber) {
          const ownNumberDigits = ownNumber.replace(/\D/g, '');
          const participantStr = String(payload.participant || '');
          const fromStr = String(payload.from || '');
          if (
            (participantStr && participantStr.includes(ownNumberDigits)) ||
            (fromStr && fromStr.includes(ownNumberDigits))
          ) {
            return;
          }
        }

        // Salva mapping @lid → @c.us se il webhook ha già normalizzato
        if (payload.originalChatId?.endsWith('@lid') && payload.chatId?.endsWith('@c.us')) {
          storeRef.current.saveLidMapping(payload.originalChatId, payload.chatId);
        } else if (payload.chatId?.endsWith('@lid') && payload.participant?.endsWith('@c.us')) {
          storeRef.current.saveLidMapping(payload.chatId, payload.participant);
        }

        // Risolvi @lid → @c.us usando lidToCusMap (WAHA 2026.3.x invia tutto come @lid)
        let resolvedChatId = payload.chatId as string;
        if (resolvedChatId?.endsWith('@lid')) {
          const mapped = storeRef.current.lidToCusMap.get(resolvedChatId);
          if (mapped) {
            resolvedChatId = mapped;
          }
        }

        const mappedStatus = mapWahaPresenceStatus(payload.presence);

        // Metodo 0: se @lid non risolto ma participant è @c.us, match diretto contro chat list
        if (resolvedChatId.endsWith('@lid') && payload.participant?.endsWith('@c.us')) {
          const participantId = String(payload.participant);
          const matchedChat = storeRef.current.chats.find(c => c.id === participantId);
          if (matchedChat) {
            resolvedChatId = participantId;
            storeRef.current.saveLidMapping(String(payload.chatId), participantId);
          }
        }

        const presenceData = {
          chatId: resolvedChatId,
          status: mappedStatus,
          lastSeen: payload.lastSeen ? new Date(payload.lastSeen) : undefined,
        };
        storeRef.current.updatePresence(resolvedChatId, presenceData);

        // Safety net @lid→@c.us: fallback se Metodo 0 non ha risolto
        if (resolvedChatId.endsWith('@lid')) {
          const participantJid = String(payload.participant || resolvedChatId);
          let matched = false;

          // Metodo 1: cerca via lastMessage.senderJid nelle chat sidebar
          const chats = storeRef.current.chats;
          for (const c of chats) {
            if (c.lastMessage?.senderJid === resolvedChatId ||
                c.lastMessage?.senderJid === participantJid) {
              storeRef.current.updatePresence(c.id, { ...presenceData, chatId: c.id });
              matched = true;
              break;
            }
          }

          // Metodo 2: se non trovato, cerca nei messaggi della chat aperta
          if (!matched && storeRef.current.selectedChatId) {
            const msgs = storeRef.current.messages;
            const hasInbound = msgs.some(m =>
              m.direction === 'inbound' &&
              (m.senderJid === resolvedChatId || m.senderJid === participantJid)
            );
            if (hasInbound) {
              storeRef.current.updatePresence(storeRef.current.selectedChatId, {
                ...presenceData,
                chatId: storeRef.current.selectedChatId,
              });
            }
          }
        }
      })
      .subscribe();

    // === 3. REACTIONS CHANNEL (broadcast) ===
    const reactionsChannel = supabase
      .channel(`reactions-${organizationId}`)
      .on('broadcast', { event: 'reaction_update' }, ({ payload }) => {
        console.log('[Realtime] Reaction broadcast received:', payload);
        storeRef.current.updateMessageReaction(payload.messageId, payload.action, payload.chatId);
      })
      .subscribe((status: string, err?: Error) => {
        if (status === 'SUBSCRIBED') {
          console.log('[Realtime] Reactions channel subscribed for org:', organizationId);
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[Realtime] Reactions channel error:', err?.message);
        }
      });

    channelsRef.current = [messagesChannel, presenceChannel, reactionsChannel];

    return () => {
      for (const ch of channelsRef.current) {
        supabase.removeChannel(ch);
      }
      channelsRef.current = [];
    };
    // SOLO organizationId come dipendenza.
    // Le funzioni store sono accedute via storeRef.current (stabile).
  }, [organizationId]);
}

async function transformDbPayload(
  row: Record<string, unknown>,
  organizationId?: string
): Promise<Message> {
  // Determina chatId in base alla direzione
  let chatId: string;
  if (row.direction === 'inbound') {
    chatId = (row.from_jid as string) || '';
  } else {
    const toJid = (row.to_jid as string) || '';
    if (!toJid) {
      // Fallback: estrai chatId dal message ID (formato: true_{remote_jid}_{unique_id})
      const messageId = (row.id as string) || '';
      const parts = messageId.split('_');
      chatId = parts.length >= 2 ? parts[1] : '';
    } else {
      chatId = toJid.includes('@') ? toJid : `${toJid}@c.us`;
    }
  }

  // Risolvi @lid → @c.us usando lidToCusMap (WAHA 2026.3.x)
  if (chatId.endsWith('@lid')) {
    const store = useWhatsAppChatStore.getState();
    const mapped = store.lidToCusMap.get(chatId);
    if (mapped) chatId = mapped;
  }

  // Decifra content e reply_to_body se criptati
  let content = (row.content as string) || '';
  const parsedReply = parseReplyTo(row.reply_to_id as string | undefined, row.reply_to_body as string | undefined);
  let replyToBody = parsedReply.replyToBody;

  // Tipi che NON richiedono decrittazione del content (messaggi media)
  const MEDIA_TYPES = ['audio', 'ptt', 'voice', 'image', 'video', 'sticker', 'document', 'location', 'contact'];

  const hasMediaType = MEDIA_TYPES.includes(row.type as string);
  const hasMediaUrl = !!row.media_url;
  const mediaMimetype = row.media_mimetype as string | undefined;
  const hasMediaMimetype = mediaMimetype && (
    mediaMimetype.startsWith('audio/') ||
    mediaMimetype.startsWith('video/') ||
    mediaMimetype.startsWith('image/') ||
    mediaMimetype.startsWith('application/')
  );
  const isMediaMessage = hasMediaType || hasMediaUrl || hasMediaMimetype;

  try {
    if (content && organizationId && !isMediaMessage && isEncrypted(content)) {
      content = await decryptWhatsAppMessage(content, organizationId);
    }
    if (replyToBody && organizationId && isEncrypted(replyToBody)) {
      replyToBody = await decryptWhatsAppMessage(replyToBody, organizationId);
    }
  } catch (error) {
    console.error('[Realtime] Decryption error:', row.id);
  }

  // Estrai sender info per gruppi dal raw_payload
  const rawPayload = row.raw_payload as Record<string, any> | undefined;
  const messagePayload = rawPayload?.payload || rawPayload;
  const isGroupMessage = chatId.endsWith('@g.us') || (row.to_jid as string)?.endsWith?.('@g.us');

  let senderJid: string | undefined;
  let senderName: string | undefined;

  if (row.direction === 'inbound') {
    if (isGroupMessage && messagePayload) {
      const extractJid = (val: any): string | undefined => {
        if (!val) return undefined;
        if (typeof val === 'string') return val;
        if (typeof val === 'object' && val._serialized) return val._serialized;
        return undefined;
      };
      senderJid =
        extractJid(messagePayload?.author) ||
        extractJid(messagePayload?._data?.author) ||
        extractJid(messagePayload?.participant) ||
        extractJid(messagePayload?._data?.participant);

      senderName = messagePayload?.notifyName ||
                   messagePayload?._data?.notifyName ||
                   messagePayload?.pushName ||
                   messagePayload?._data?.pushName ||
                   undefined;
    } else {
      senderJid = (row.from_jid as string) || undefined;
    }
  }

  return {
    id: row.id as string,
    chatId,
    content,
    type: normalizeDbMessageType(row.type) as MessageType,
    direction: row.direction as 'inbound' | 'outbound',
    status: dbMessageStatus(row),
    timestamp: new Date(row.created_at as string),
    mediaUrl: row.media_url as string | undefined,
    mediaMimetype: row.media_mimetype as string | undefined,
    replyToId: parsedReply.replyToId,
    replyToBody,
    senderJid,
    senderName,
    isEdited: row.is_edited as boolean | undefined,
    isDeleted: row.is_deleted as boolean | undefined,
    ...rowExtras(row),
  };
}
